export function calculateBundleTotals(items) {
  return items.reduce(
    (totals, item) => {
      const price = Number(item.price) || 0;
      const compareAtPrice = Math.max(
        price,
        Number(item.compareAtPrice) || price,
      );

      totals.price += price;
      totals.referencePrice += compareAtPrice;
      totals.savings += compareAtPrice - price;
      return totals;
    },
    { price: 0, referencePrice: 0, savings: 0 },
  );
}

export function formatSavingsMessage(template, amount) {
  return String(template || "")
    .replace("[amount]", amount)
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function buildCartPayload(items, sections = [], sectionsUrl = "") {
  const seen = new Set();
  const cartItems = items.map((item) => {
    const id = String(item.variantId);
    if (seen.has(id)) {
      throw new Error(`Duplicate bundle variant: ${id}`);
    }
    seen.add(id);

    return {
      id,
      quantity: Math.max(1, Number.parseInt(item.quantity, 10) || 1),
    };
  });

  const payload = { items: cartItems };
  if (sections.length) payload.sections = sections;
  if (sectionsUrl) payload.sections_url = sectionsUrl;
  return payload;
}

const HTMLElementBase =
  typeof HTMLElement === "undefined" ? class {} : HTMLElement;

class ProductBundleSaveMore extends HTMLElementBase {
  connectedCallback() {
    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";

    this.items = [...this.querySelectorAll("[data-bundle-item]")];
    this.submitButton = this.querySelector("[data-bundle-submit]");
    this.totalElement = this.querySelector("[data-bundle-total]");
    this.savingsElement = this.querySelector("[data-bundle-savings]");
    this.statusElement = this.querySelector("[data-bundle-status]");

    this.addEventListener("change", (event) => {
      if (event.target.matches("[data-bundle-checkbox], [data-bundle-variant]")) {
        this.updateItem(event.target.closest("[data-bundle-item]"));
        this.render();
      }
    });
    this.submitButton?.addEventListener("click", () => this.addSelectedItems());

    this.items.forEach((item) => this.updateItem(item));
    this.render();
  }

  getVariantControl(item) {
    return item?.querySelector("[data-bundle-variant]");
  }

  getVariantData(item) {
    const control = this.getVariantControl(item);
    const source =
      control?.tagName === "SELECT"
        ? control.options[control.selectedIndex]
        : control;

    return {
      variantId: source?.dataset.variantId || control?.value || "",
      price: Number(source?.dataset.price) || 0,
      compareAtPrice: Number(source?.dataset.compareAtPrice) || 0,
      imageUrl: source?.dataset.imageUrl || "",
      quantity: 1,
    };
  }

  isSelected(item) {
    const checkbox = item.querySelector("[data-bundle-checkbox]");
    return checkbox?.checked !== false;
  }

  getSelectedItems() {
    return this.items
      .filter((item) => this.isSelected(item))
      .map((item) => this.getVariantData(item))
      .filter((item) => item.variantId);
  }

  formatMoney(amount) {
    if (window.T4SThemeSP?.Currency?.formatMoney) {
      return window.T4SThemeSP.Currency.formatMoney(
        amount,
        this.dataset.moneyFormat,
      );
    }
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(amount, this.dataset.moneyFormat);
    }
    return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "USD",
    }).format(amount / 100);
  }

  renderOffBadge(compareAtPrice, price, label) {
    if (!(compareAtPrice > price) || compareAtPrice <= 0) return "";
    const pct = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
    if (pct <= 0) return "";
    const text = `${pct}% ${label || "OFF"}`;
    return `<span style="display:inline-block;padding:2px 5px;background:#5B1724;color:#fff;font-size:11px;font-weight:600;white-space:nowrap">${text}</span>`;
  }

  updateItem(item) {
    if (!item) return;
    const variant = this.getVariantData(item);
    const image = item.querySelector("[data-bundle-image]");
    const price = item.querySelector("[data-bundle-item-price]");

    if (image && variant.imageUrl) {
      image.src = variant.imageUrl;
      image.removeAttribute("srcset");
    }
    if (price) {
      price.innerHTML =
        variant.compareAtPrice > variant.price
          ? `<del>${this.formatMoney(variant.compareAtPrice)}</del><ins>${this.formatMoney(variant.price)}</ins>${this.renderOffBadge(variant.compareAtPrice, variant.price, price.dataset.zlOffLabel)}`
          : `<ins>${this.formatMoney(variant.price)}</ins>`;
    }
  }

  render() {
    const selectedItems = this.getSelectedItems();
    const totals = calculateBundleTotals(selectedItems);

    if (this.totalElement) {
      this.totalElement.innerHTML =
        totals.referencePrice > totals.price
          ? `<del>${this.formatMoney(totals.referencePrice)}</del><ins>${this.formatMoney(totals.price)}</ins>${this.renderOffBadge(totals.referencePrice, totals.price, this.totalElement.dataset.zlOffLabel)}`
          : `<ins>${this.formatMoney(totals.price)}</ins>`;
    }

    if (this.savingsElement) {
      this.savingsElement.hidden = totals.savings <= 0;
      this.savingsElement.textContent =
        totals.savings > 0
          ? formatSavingsMessage(
              this.dataset.savedTemplate,
              this.formatMoney(totals.savings),
            )
          : "";
    }

    if (this.submitButton) {
      const count = selectedItems.length;
      this.submitButton.disabled = count === 0;
      this.submitButton.textContent =
        count === 1
          ? this.dataset.addOneTemplate
          : this.dataset.addItemsTemplate.replace("[count]", count);
    }
  }

  async addSelectedItems() {
    if (!this.submitButton || this.submitButton.disabled) return;

    const originalText = this.submitButton.textContent;
    this.submitButton.disabled = true;
    this.submitButton.setAttribute("aria-busy", "true");
    this.submitButton.textContent = this.dataset.addingText;
    if (this.statusElement) this.statusElement.textContent = "";

    try {
      const payload = buildCartPayload(
        this.getSelectedItems(),
        ["cart_data", "mini_cart"],
        window.location.pathname,
      );
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || "Cart request failed");
      }

      const result = await response.json();
      if (result.sections && window.T4SThemeSP?.Cart?.renderContents) {
        window.T4SThemeSP.Cart.renderContents(result.sections);
      } else if (window.T4SThemeSP?.Cart?.getToFetch) {
        window.T4SThemeSP.Cart.getToFetch();
      }
      if (this.statusElement) {
        this.statusElement.textContent = this.dataset.addedText;
      }
      this.submitButton.textContent = this.dataset.addedText;

      const themeJquery = window.jQuery_T4NT;
      if (typeof themeJquery === "function") {
        const drawerTrigger = themeJquery(
          '[data-drawer-options*="#t4s-mini_cart"]:visible',
        ).first();
        if (drawerTrigger.length) {
          drawerTrigger.trigger("click.drawer");
        } else if (window.T4SThemeSP?.Drawer?.opend) {
          window.T4SThemeSP.Drawer.opend(themeJquery("#t4s-mini_cart"));
        }
      }
    } catch (error) {
      console.error(error);
      if (this.statusElement) {
        this.statusElement.textContent = this.dataset.errorText;
      }
      this.submitButton.textContent = this.dataset.errorText;
    } finally {
      this.submitButton.removeAttribute("aria-busy");
      window.setTimeout(() => {
        this.submitButton.disabled = false;
        this.submitButton.textContent = originalText;
        this.render();
      }, 1200);
    }
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("product-bundle-save-more")
) {
  customElements.define("product-bundle-save-more", ProductBundleSaveMore);
}
